/**
 * Step 1: Basic Information
 * 
 * Promo basic information form:
 * - Promo Name (required)
 * - Promo Code (optional for AUTOMATIC, required for CODE_BASED)
 * - Discount Mode (AUTOMATIC or CODE_BASED)
 * - Domain (required)
 * - Transaction Type (required)
 * - Link to Campaign (optional)
 */

import React, { useMemo, useEffect, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { PromoFormData, PromoValidationErrors, PromoDomain, TransactionType, DiscountMode, PromoVisibilityType } from '../../../../../api/promos/types';
import { getCampaigns } from '../../../../../api/promos/campaignService';
import { CampaignStatus } from '../../../../../api/promos/types/campaignTypes';
import '../../../../../Common/FormStyles.css';
import '../../../Campaigns/CreateCampaign/components/StepperButtons.css';
import '../../../Campaigns/CreateCampaign/components/StepperFormStyles.css';
import './DiscountModeButtons.css';

interface StepBasicInfoProps {
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  onChange: (updates: Partial<PromoFormData>) => void;
  onNext: () => void;
  onCancel: () => void;
}

const StepBasicInfo: React.FC<StepBasicInfoProps> = ({
  formData,
  validationErrors,
  onChange,
  onNext,
  onCancel,
}) => {
  // State for campaigns dropdown
  const [campaigns, setCampaigns] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  // Common styling constants for react-select (matching filter dropdowns)
  const FILTER_HEIGHT = '38px';
  const FILTER_FONT_SIZE = '14px';
  const BORDER_COLOR = '#e3e6f0';

  // React-select styles matching filter dropdowns
  const getSelectStyles = () => ({
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any, state: any) => ({
      ...base,
      height: FILTER_HEIGHT,
      minHeight: FILTER_HEIGHT,
      border: `1px solid ${BORDER_COLOR}`,
      borderRadius: '6px',
      fontSize: FILTER_FONT_SIZE,
      boxShadow: 'none',
      width: '100%',
      minWidth: '150px',
      borderColor: BORDER_COLOR,
      '&:hover': {
        borderColor: BORDER_COLOR
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center'
    }),
    input: (base: any) => ({
      ...base,
      margin: '0',
      paddingTop: '0',
      paddingBottom: '0',
      height: 'auto'
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0',
      svg: {
        width: '16px',
        height: '16px'
      }
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#212529',
      fontSize: FILTER_FONT_SIZE,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 'calc(100% - 20px)'
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#6c757d',
      fontSize: FILTER_FONT_SIZE
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#e9ecef'
        : state.isFocused 
          ? '#f8f9fa'
          : 'white',
      color: '#212529',
      fontSize: FILTER_FONT_SIZE,
      '&:hover': {
        backgroundColor: '#f8f9fa'
      }
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999
    })
  });

  // Handle input changes
  const handleInputChange = (field: keyof PromoFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value;
    onChange({
      [field]: value
    } as Partial<PromoFormData>);
  };

  // Handle select changes for react-select
  const handleDomainChange = (selectedOption: any) => {
    const updates: Partial<PromoFormData> = {
      domain: selectedOption ? selectedOption.value : ''
    };
    // Clear transaction type when domain changes
    if (!selectedOption || formData.domain !== selectedOption.value) {
      updates.transactionType = '' as TransactionType;
    }
    onChange(updates);
  };

  const handleTransactionTypeChange = (selectedOption: any) => {
    onChange({
      transactionType: selectedOption ? selectedOption.value : ''
    } as Partial<PromoFormData>);
  };

  const handleCampaignChange = (selectedOption: any) => {
    onChange({
      campaignId: selectedOption ? selectedOption.value : ''
    } as Partial<PromoFormData>);
  };

  const handleVisibilityChange = (type: PromoVisibilityType) => {
    onChange({
      visibility: { type },
    } as Partial<PromoFormData>);
  };

  // Fetch active campaigns on mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        const response = await getCampaigns({
          status: CampaignStatus.ACTIVE,
          pageSize: 100,
          pageNum: 1,
        });
        const campaignOptions = (response.campaigns || []).map(campaign => ({
          value: campaign.id,
          label: campaign.name,
        }));
        setCampaigns(campaignOptions);
      } catch (error) {
        setCampaigns([]);
      } finally {
        setLoadingCampaigns(false);
      }
    };
    fetchCampaigns();
  }, []);

  // Domain options
  const domainOptions = [
    { value: PromoDomain.SHOP, label: 'Shop' },
    { value: PromoDomain.ACADEMY, label: 'Academy' },
    { value: PromoDomain.BOOKING, label: 'Venue Booking' }
  ];

  // Transaction Type options - filtered based on selected domain
  const getTransactionTypeOptions = () => {
    if (!formData.domain) {
      return [];
    }
    
    const domainTransactionMap: Record<PromoDomain, TransactionType[]> = {
      [PromoDomain.SHOP]: [TransactionType.SHOP_PURCHASE],
      [PromoDomain.ACADEMY]: [TransactionType.ACADEMY_ENROLLMENT],
      [PromoDomain.BOOKING]: [TransactionType.VENUE_BOOKING],
      [PromoDomain.COACHING]: [TransactionType.COACHING_ENROLLMENT],
    };
    
    const allowedTypes = domainTransactionMap[formData.domain] || [];
    
    const options = [
      { value: TransactionType.SHOP_PURCHASE, label: 'Shop Purchase' },
      { value: TransactionType.ACADEMY_ENROLLMENT, label: 'Academy Enrollment' },
      { value: TransactionType.VENUE_BOOKING, label: 'Venue Booking' },
      { value: TransactionType.COACHING_ENROLLMENT, label: 'Coaching Enrollment' }
    ];
    
    return options.filter(opt => allowedTypes.includes(opt.value));
  };

  const transactionTypeOptions = getTransactionTypeOptions();

  // Check if required fields are filled
  const isNextDisabled = useMemo(() => {
    const hasName = Boolean(formData.name?.trim());
    const hasDomain = Boolean(formData.domain);
    
    // Check if transaction type is valid for selected domain
    const validTransactionTypes = getTransactionTypeOptions();
    const hasValidTransactionType = Boolean(
      formData.transactionType && 
      validTransactionTypes.some(opt => opt.value === formData.transactionType)
    );
    
    const hasCode = formData.discountMode === DiscountMode.CODE_BASED 
      ? Boolean(formData.code?.trim())
      : true; // Code not required for AUTOMATIC
    
    return !hasName || !hasDomain || !hasValidTransactionType || !hasCode;
  }, [formData.name, formData.code, formData.domain, formData.transactionType, formData.discountMode]);

  return (
    <form autoComplete="off" noValidate>
      {/* Step Header */}
      <div className="mb-4">
        <h5 className="mb-2">Basic Information</h5>
        <p className="text-muted mb-0">
          Configure the core details of your promo
        </p>
      </div>

      {/* Form Fields */}
      <Row>
        {/* Promo Name and Promo Code in single line */}
        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Promo Name (Internal) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              className="stepper-form-control"
              placeholder="Enter promo name"
              value={formData.name}
              onChange={handleInputChange('name')}
              isInvalid={!!validationErrors.name}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Promo Code <span className={formData.discountMode === DiscountMode.CODE_BASED ? 'text-danger' : 'text-muted'}>
                {formData.discountMode === DiscountMode.CODE_BASED ? '*' : '(Optional)'}
              </span>
            </Form.Label>
            <Form.Control
              type="text"
              className="stepper-form-control"
              placeholder="Enter promo code"
              value={formData.code || ''}
              onChange={handleInputChange('code')}
              isInvalid={!!validationErrors.code}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.code}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* Discount Mode */}
        <Col lg={12} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Discount Mode</Form.Label>
            <div className="discount-mode-buttons-container">
              <button
                type="button"
                className={`discount-mode-button ${
                  formData.discountMode === DiscountMode.AUTOMATIC ? 'selected' : ''
                }`}
                onClick={() => onChange({ discountMode: DiscountMode.AUTOMATIC })}
              >
                <div className="discount-mode-radio-button"></div>
                <div className="discount-mode-button-content">
                  <span className="discount-mode-button-text">Automatic</span>
                  <div className="discount-mode-description">
                    Applied automatically to eligible orders
                  </div>
                </div>
              </button>
              <button
                type="button"
                className={`discount-mode-button ${
                  formData.discountMode === DiscountMode.CODE_BASED ? 'selected' : ''
                }`}
                onClick={() => onChange({ discountMode: DiscountMode.CODE_BASED })}
              >
                <div className="discount-mode-radio-button"></div>
                <div className="discount-mode-button-content">
                  <span className="discount-mode-button-text">Code Based</span>
                  <div className="discount-mode-description">
                    Requires a promo code
                  </div>
                </div>
              </button>
            </div>
          </Form.Group>
        </Col>

        {/* Domain and Transaction Type in single line */}
        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Domain <span className="text-danger">*</span>
            </Form.Label>
            <Select
              styles={getSelectStyles()}
              options={domainOptions}
              value={domainOptions.find(opt => opt.value === formData.domain) || null}
              onChange={handleDomainChange}
              placeholder="Select Domain"
              menuPortalTarget={document.body}
              isSearchable={false}
            />
            {validationErrors.domain && (
              <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                {validationErrors.domain}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Transaction Type <span className="text-danger">*</span>
            </Form.Label>
            <Select
              styles={getSelectStyles()}
              options={transactionTypeOptions}
              value={transactionTypeOptions.find(opt => opt.value === formData.transactionType) || null}
              onChange={handleTransactionTypeChange}
              placeholder="Select Transaction Type"
              menuPortalTarget={document.body}
              isSearchable={false}
            />
            {validationErrors.transactionType && (
              <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                {validationErrors.transactionType}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Link to Campaign */}
        <Col lg={12} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Link to Campaign <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Select
              styles={getSelectStyles()}
              options={campaigns}
              value={campaigns.find(opt => opt.value === formData.campaignId) || null}
              onChange={handleCampaignChange}
              placeholder={loadingCampaigns ? "Loading campaigns..." : "Select Campaign"}
              menuPortalTarget={document.body}
              isSearchable={true}
              isClearable={true}
            />
            {validationErrors.campaignId && (
              <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                {validationErrors.campaignId}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Visibility */}
        <Col lg={12} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Visibility</Form.Label>
            <div className="discount-mode-buttons-container">
              <button
                type="button"
                className={`discount-mode-button ${
                  (formData.visibility?.type || PromoVisibilityType.PUBLIC) === PromoVisibilityType.PUBLIC ? 'selected' : ''
                }`}
                onClick={() => handleVisibilityChange(PromoVisibilityType.PUBLIC)}
              >
                <div className="discount-mode-radio-button"></div>
                <div className="discount-mode-button-content">
                  <span className="discount-mode-button-text">Public</span>
                </div>
              </button>
              <button
                type="button"
                className={`discount-mode-button ${
                  formData.visibility?.type === PromoVisibilityType.ASSIGNED ? 'selected' : ''
                }`}
                onClick={() => handleVisibilityChange(PromoVisibilityType.ASSIGNED)}
              >
                <div className="discount-mode-radio-button"></div>
                <div className="discount-mode-button-content">
                  <span className="discount-mode-button-text">Assigned to Specific Users</span>
                </div>
              </button>
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Navigation Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <div></div>
        <button
          className="stepper-next-button"
          onClick={onNext}
          disabled={isNextDisabled}
        >
          Next
          <i className="ph-arrow-right"></i>
        </button>
      </div>
    </form>
  );
};

export default StepBasicInfo;
